import { useCallback, useContext, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AuthContext from "../../../contexts/AuthContext";
import { JoinService } from "@/core/http/services/JoinService";

const REDIRECT_PATH = "/user/redirect";

export const useLogin = () => {
  const { userLoggedData, signIn, updateAuthPatch } = useContext(AuthContext);
  const [getErr, setErr] = useState(false);
  const [getErrMsg, setErrMsg] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { inviteToken: inviteTokenParam } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const inviteTokenFromQuery = searchParams.get("inviteToken");
  const inviteTokenFromPathFallback = (() => {
    const pathname = location?.pathname || "";
    const match = pathname.match(/^\/login\/([^/]+)\/?$/);
    return match?.[1] || null;
  })();

  const inviteToken =
    inviteTokenParam ||
    inviteTokenFromQuery ||
    inviteTokenFromPathFallback ||
    null;

  const tryJoinClub = useCallback(async () => {
    if (!inviteToken) {
      return { ok: true };
    }

    const { status, data, message } = await JoinService.join(inviteToken);
    if (status !== 200) {
      return {
        ok: false,
        message: message || "Falha ao entrar no clube.",
      };
    }

    const patch = {};
    if (Array.isArray(data?.clubs)) patch.clubs = data.clubs;
    if (data?.selectedClubId) patch.selectedClubId = data.selectedClubId;
    if (Object.keys(patch).length > 0) {
      await updateAuthPatch(patch);
    }

    return { ok: true };
  }, [inviteToken, updateAuthPatch]);

  const verifyLogin = useCallback(async () => {
    if (userLoggedData) {
      setLoading(true);
      setErr(false);
      setErrMsg("");
      const joinResult = await tryJoinClub();
      setLoading(false);
      if (!joinResult.ok) {
        setErr(true);
        setErrMsg(joinResult.message);
      }
      navigate(REDIRECT_PATH);
    }
  }, [navigate, tryJoinClub, userLoggedData]);

  useEffect(() => {
    verifyLogin();
  }, [verifyLogin]);

  const handleSignIn = async (event) => {
    event.preventDefault();
    setErr(false);
    setErrMsg("");
    setSuccessMessage("");
    setLoading(true);

    const data = new FormData(event.currentTarget);

    const payload = {
      email: data.get("email")?.trim(),
      password: data.get("password"),
    };

    try {
      const result = await signIn(payload);

      if (result?.success) {
        const joinResult = await tryJoinClub();
        if (!joinResult.ok) {
          setErr(true);
          setErrMsg(joinResult.message);
        }
        setSuccessMessage(result.message || "Login realizado com sucesso!");
        navigate(REDIRECT_PATH);
        return;
      }

      setErr(true);
      setErrMsg(result?.message || "Dados incorretos.");
    } catch (error) {
      setErr(true);
      setErrMsg(error?.message || "Erro inesperado ao realizar login.");
    } finally {
      setLoading(false);
    }
  };

  return {
    verifyLogin,
    handleSignIn,
    getErr,
    getErrMsg,
    successMessage,
    loading,
  };
};
